'use client'

import type { FC } from 'react'
import { Trash2 } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import SelectMenu from 'components/SelectMenu'
import { Button } from 'components/ui/button'
import { Input } from 'ui/input'
import { Label } from 'ui/label'
import FieldWrapper from './FieldWrapper'
import type { FieldRendererProps } from './types'

export const menuArrayFieldConfig = {
    getNewArrayItem: () => ({ id: uuidv4(), type: '', text: '', link: '' }),
    extraPropsKind: 'array' as const
} satisfies Partial<import('./types').FieldTypeConfig>

const MenuArrayField: FC<FieldRendererProps> = ({
    field,
    value,
    error,
    formState,
    onArrayChange,
    onAddArrayItem,
    onRemoveArrayItem
}) => {
    const items = (value || []) as { id?: string; type?: string; text?: string; link?: string }[]
    const arr = (formState?.[field.name] ?? items) as unknown[]

    return (
        <FieldWrapper fieldName={field.name} error={error}>
            <div className='space-y-2'>
                <Label>{field.label}</Label>
                {items.map((item: typeof items[0], index: number) => {
                    const it = { ...item }
                    if (!it.id) it.id = uuidv4()
                    return (
                        <div key={it.id} className='flex flex-col items-center space-x-2'>
                            <div className='flex space-x-2 w-full'>
                                <SelectMenu
                                    name={`${field.name}[${index}].type`}
                                    label='Type'
                                    items={(field.options || [])
                                        .filter(
                                            opt =>
                                                opt.id === it.type ||
                                                !items.some(
                                                    (link: typeof it) =>
                                                        link.type === opt.id && link.id !== it.id
                                                )
                                        )
                                        .map(opt => ({ id: opt.id, name: opt.name }))}
                                    value={it.type ?? ''}
                                    set={v => {
                                        const opt = field.options?.find(o => o.id === v)
                                        onArrayChange?.(field.name, index, field, {
                                            ...it,
                                            type: v,
                                            link: opt?.link
                                        })
                                    }}
                                />
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    type='button'
                                    onClick={() => onRemoveArrayItem?.(field.name, index, field)}
                                    disabled={!!field.min && arr.length <= field.min}
                                    className='h-8 w-8'
                                >
                                    <Trash2 className='h-4 w-4' />
                                </Button>
                            </div>
                            {it.type === 'external' && (
                                <div className='flex flex-col w-[90%] gap-y-2 mt-2'>
                                    <Input
                                        placeholder='Name'
                                        value={it.text ?? ''}
                                        onChange={e =>
                                            onArrayChange?.(field.name, index, field, {
                                                ...it,
                                                text: e.target.value
                                            })
                                        }
                                    />
                                    <Input
                                        placeholder='Link (e.g https://example.com)'
                                        value={it.link ?? ''}
                                        onChange={e =>
                                            onArrayChange?.(field.name, index, field, {
                                                ...it,
                                                link: e.target.value
                                            })
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    )
                })}
                <div className='flex items-items mt-2 gap-2 flex-col'>
                    <Button
                        type='button'
                        onClick={() => onAddArrayItem?.(field.name, field)}
                        disabled={!!field.max && arr.length >= field.max}
                    >
                        Add Link
                    </Button>
                    <span>
                        {!!field.min && arr.length < field.min && (
                            <p className='text-destructive text-sm'>
                                Minimum {field.min} links required
                            </p>
                        )}
                        {field.max && arr.length >= field.max && (
                            <p className='text-warning text-sm'>
                                Maximum allowed links ({field.max}) reached
                            </p>
                        )}
                    </span>
                </div>
            </div>
        </FieldWrapper>
    )
}

export default MenuArrayField
